import classNames from 'classnames';
import type { ReactNode } from 'react';
import React from 'react';
import type { OrgReducer } from '../types/orgTypes';
import type { Org } from '../types/ts/orgTypes';
import { AGENCY_ORG_TYPE } from './viewConstants';

/* --------------------- Relationship Inheritance ------------------------- */
export const VENDOR_ORGANIZATION_INHERITANCE = 'VENDOR-ORGANIZATION';
export const VENDOR_APP_INHERITANCE = 'VENDOR-APP';
export const USER_ORGANIZATION_INHERITANCE = 'USER-ORGANIZATION';
export const USER_APP_INHERITANCE = 'USER-APP';

/* ----------------------------- Constants -------------------------------- */
export const SAVE_OPTIONS = {
  INHERITING_APPS: 'inheriting-apps',
  INHERITING_PLUS_SPECIFIC_APPS: 'inheriting-plus-specific-apps',
  ALL_APPS: 'all-apps',
};

/* ----------------------------- Text -------------------------------- */
export const ORGANIZATION_TEXT = {
  NO_ORG_SCREEN: {
    TITLE: 'This page is not available in Organization view',
    BODY: 'Please use the app selector in the top left to view this page for a specific app.',
  },
  MODAL: {
    TITLE: 'Organization Settings',
    DROPDOWN: {
      LABEL: 'Apply this setting to',
      TOOLTIP:
        'Choose which apps you would like to apply this setting to. By default, the setting will be applied to apps that do not have an individual setting and currently inherit their setting from the parent organization {org}. You can choose to override some or all app-specific settings here so they inherit from the organization as well.',
      INHERITING_APPS: 'All apps that inherit from the organization',
      INHERITING_PLUS_SPECIFIC_APPS: 'Apps that inherit plus specific apps',
      ALL_APPS: 'All apps',
    },
    ADDITIONAL_APPS: {
      LABEL: 'Add specific apps',
    },
  },
};

const TEXT_OPTIONS = {
  [SAVE_OPTIONS.INHERITING_APPS]: 'all apps that inherit form the organization',
  [SAVE_OPTIONS.INHERITING_PLUS_SPECIFIC_APPS]: 'the apps that you have selected',
  [SAVE_OPTIONS.ALL_APPS]: 'all apps',
};

const OVERRIDE_MESSAGE_PART = 'This will override any settings that are configured at the app level for';

/* ------------------ Helper Methods / Text Components --------------------- */
export type SelectedOptionValue = 'inheriting-apps' | 'inheriting-plus-specific-apps' | 'all-apps';

export const getMessageText = (selectedOption: SelectedOptionValue, plainText?: boolean, appNames?: string[]): ReactNode => {
  const optionText = TEXT_OPTIONS[selectedOption];

  if (plainText) {
    const apps = appNames && appNames.length > 0 ? ` (${appNames.join(', ')})` : '';
    return `${OVERRIDE_MESSAGE_PART} ${optionText}${apps}. You cannot undo this once you click Save.`;
  }

  return (
    <span>
      {OVERRIDE_MESSAGE_PART} {optionText}.{' '}
      <span>
        You <span className="h_fw-700">cannot undo</span> this once you click Save.
      </span>
    </span>
  );
};

// Some orgs might not have names, so we want to allow for that scenario
export const getDefaultToggleText = (inheriting: boolean, orgName?: string) => (
  <span>
    <span className={classNames({ 'h_fw-700': inheriting })}>You are{inheriting ? '' : ' not'} inheriting</span> these
    settings from your app's parent organization{orgName ? ', ' + orgName : ''}.
    {inheriting
      ? ` In order to edit, please change this toggle to not inherit from the organization, or use the selector in the top left to make changes for the entire${
          orgName ? ' ' + orgName : ''
        } organization.`
      : ''}
  </span>
);

export const orgIsAgency = (org?: Org): boolean => {
  if (!org) {
    return false;
  }
  return org.organization_type === AGENCY_ORG_TYPE;
};

export const hideAppInheritanceElements = (org: OrgReducer): boolean =>
  org.userOnOrgView || org.userOnAgencyView || !org.currentOrg;
